import React, { useMemo } from 'react'
import { format, addDays, isSameDay, isWeekend, startOfDay } from 'date-fns'
import { hasAvailableSlots } from './timeSlots'
import { ChevronLeftIcon, ChevronRightIcon } from './icons'
import './WeekDatePicker.scss'

interface WeekDatePickerProps {
  selectedDate: Date | null
  onSelectDate: (date: Date) => void
}

interface NavButtonProps {
  direction: 'prev' | 'next'
  onClick: () => void
  disabled: boolean
}

const NavButton = ({ direction, onClick, disabled }: NavButtonProps) => (
  <button
    className={`week_date_picker_nav week_date_picker_nav_${direction} week_date_picker_nav_grid`}
    onClick={onClick}
    disabled={disabled}
    aria-label={direction === 'prev' ? 'Previous dates' : 'Next dates'}
  >
    {direction === 'prev' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
  </button>
)

function WeekDatePicker ({ selectedDate, onSelectDate }: WeekDatePickerProps) {
  const today = useMemo(() => startOfDay(new Date()), [])
  const [startIndex, setStartIndex] = React.useState(0)

  // Generate the next 5 weekdays (Mon-Fri), skipping weekends
  const dates = useMemo(() => {
    const weekdays: Date[] = []
    let candidate = today
    while (weekdays.length < 5) {
      if (!isWeekend(candidate)) {
        weekdays.push(candidate)
      }
      candidate = addDays(candidate, 1)
    }
    return weekdays
  }, [today])

  const handlePrevious = () => {
    if (startIndex > 0) {
      setStartIndex(startIndex - 1)
    }
  }

  const handleNext = () => {
    if (startIndex < dates.length - 3) {
      setStartIndex(startIndex + 1)
    }
  }

  return (
    <div className="week_date_picker">
      <div className="week_date_picker_header">
        Available Dates
      </div>
      <div className="week_date_picker_grid">
        <NavButton direction="prev" onClick={handlePrevious} disabled={startIndex === 0} />
        <div className="week_date_picker_dates_wrapper">
          {dates.map((date, index) => {
            const isSelected = selectedDate && isSameDay(date, selectedDate)
            const dayName = format(date, 'EEE')
            const dayNumber = format(date, 'd')
            const isAvailable = hasAvailableSlots(date)
            const isVisibleOnMobile = index >= startIndex && index < startIndex + 3

            return (
              <div
                key={date.toString()}
                className={`week_date_picker_column ${!isVisibleOnMobile ? 'week_date_picker_column_hidden_mobile' : ''}`}
              >
                <div className="week_date_picker_day_name">{dayName}</div>
                <button
                  className={`week_date_picker_date ${isSelected ? 'week_date_picker_date_selected' : ''} ${!isAvailable ? 'week_date_picker_date_disabled' : ''}`}
                  onClick={() => isAvailable && onSelectDate(date)}
                  disabled={!isAvailable}
                >
                  <div className="week_date_picker_day_number">{dayNumber}</div>
                </button>
              </div>
            )
          })}
        </div>
        <NavButton direction="next" onClick={handleNext} disabled={startIndex >= dates.length - 3} />
      </div>
    </div>
  )
}

export default WeekDatePicker
